"use client";

import { usePostHog } from "posthog-js/react";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight, Terminal } from "@/components/icons";
import { cn } from "@/lib/utils";
import { buttonVariants, SectionHeader } from "@/components/ui";
import { REGISTER_URL } from "@/lib/constants";
import { MIGRATION_STEPS, MIGRATION_CURL } from "./data";

const MotionDiv = motion.div;
const MotionLi = motion.li;

export default function OxylabsMigrationGuide() {
  const prefersReducedMotion = useReducedMotion();
  const posthog = usePostHog();

  const fadeUp = (delay = 0) => ({
    initial: prefersReducedMotion ? {} : { opacity: 0, y: 24 },
    whileInView: prefersReducedMotion ? {} : { opacity: 1, y: 0 },
    viewport: scrollRevealViewport,
    transition: { duration: 0.55, delay },
  });

  return (
    <section className="py-section border-border border-b" aria-labelledby="migration-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv {...fadeUp(0)} className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Migration Guide"
            id="migration-heading"
            heading="Switching from Oxylabs takes minutes, not days."
            body="Proxy credentials are standard, so nothing about your stack needs to change except the endpoint and auth."
          />
        </MotionDiv>

        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr]">
          <ol className="space-y-8">
            {MIGRATION_STEPS.map((step, i) => (
              <MotionLi
                key={step.title}
                {...fadeUp(Math.min(i * 0.05, 0.25))}
                className="flex gap-4"
              >
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold"
                  style={{
                    backgroundColor:
                      "color-mix(in srgb, var(--brand-green) 12%, var(--background))",
                    borderColor: "color-mix(in srgb, var(--brand-green) 30%, transparent)",
                    color: "var(--brand-green)",
                  }}
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <div>
                  <h3 className="text-foreground text-base font-semibold">{step.title}</h3>
                  <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </MotionLi>
            ))}
          </ol>

          <MotionDiv {...fadeUp(0.15)} className="lg:sticky lg:top-28 lg:self-start">
            <div className="bg-card card-wash card-hover border-border relative overflow-hidden rounded-2xl border p-6">
              <div className="text-muted-foreground relative mb-3 flex items-center gap-2 text-xs font-medium">
                <Terminal size={13} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
                Verify your new proxy in one line
              </div>
              <pre className="border-border relative overflow-x-auto rounded-lg border bg-[#0a0a0b] p-4 text-xs leading-relaxed [--border:#27272a] [--foreground:#f7f7f8]">
                <code className="text-foreground">{MIGRATION_CURL}</code>
              </pre>
              <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
                The dashboard generates this for you with real credentials. Copy, paste, and
                you&rsquo;re live.
              </p>
              <a
                href={REGISTER_URL}
                onClick={() =>
                  posthog?.capture("alternatives_cta_clicked", {
                    competitor: "oxylabs",
                    location: "migration",
                  })
                }
                className={cn(buttonVariants({ variant: "default", size: "lg" }), "mt-5 w-full")}
              >
                Get your credentials
                <ArrowRight size={16} className="ml-2" aria-hidden="true" />
              </a>
            </div>
          </MotionDiv>
        </div>
      </div>
    </section>
  );
}
